import { HttpBackend, HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ApiHealthService {
    // cache of URL → "is healthy?"
    private statusCache = new Map<string, Observable<boolean>>();

    // a HttpClient that skips all interceptors
    private httpNoInterceptor: HttpClient;
    private endpointStatus = new Map<string, boolean>();

    public constructor(
        private http: HttpClient,
        private httpBackend: HttpBackend,
    ) {
        this.httpNoInterceptor = new HttpClient(httpBackend);
    }

    /**
     * Checks (once) whether `url` responds successfully to a HEAD.
     * Caches the Observable so subsequent calls reuse the result.
     */
    public isEndpointAvailable(url: string): Observable<boolean> {
        if (!this.statusCache.has(url)) {
            const check$ = this.httpNoInterceptor
                               .head<HttpResponse<unknown>>(url, { observe: 'response' })
                               .pipe(
                                   map(resp => {
                                       const status = resp.status;
                                       // 2xx → up
                                       if (status >= 200 && status < 300) {
                                           return true;
                                       }

                                       // 3xx → up (redirects mean “exists”)
                                       if (status >= 300 && status < 400) {
                                           return true;
                                       }

                                       // otherwise → treat as down
                                       return false;
                                   }),
                                   catchError(err => {
                                       const s = err.status;
                                       // Treat these as “up” because endpoint exists but HEAD isn’t allowed or you’re unauthorized
                                       if (
                                           s === 401 || // Unauthorized
                                           s === 403 || // Forbidden
                                           s === 405 || // Method Not Allowed
                                           (s >= 300 && s < 400)
                                       ) {
                                           return of(true);
                                       }

                                       // 404, 5xx, network error → down
                                       return of(false);
                                   }),
                                   // cache the last emitted boolean
                                   shareReplay(1),
                               );
            this.statusCache.set(url, check$);
        }

        return this.statusCache.get(url)!;
    }

    public checkEndpoints(urls: string[]): Observable<void> {
        return forkJoin(
            urls.map(u =>
                this.isEndpointAvailable(u).pipe(
                    tap(up => this.endpointStatus.set(u, up))
                )
            )
        ).pipe(map(() => {}));
    }

    public shouldMock(url: string): boolean {
        // if it’s known up → false; if missing or known down → true
        // TODO: need to properly map `url` to those urls in `this.endpointStatus`.
        //  At the current stage, it's an exact match and thus this.endpointStatus.get(url) will always return undefined.
        console.log(url, this.endpointStatus);
        return this.endpointStatus.get(url) !== true;
    }
}
